import { useMemo, useState } from 'react';
import { LuCheck, LuSlidersHorizontal } from 'react-icons/lu';
import { toast } from 'sonner';

import {
    AccountFee,
    BillingGroupRun,
    FeeType,
    IntelligentStrategyType,
    ManualFeeAmendmentRequest,
} from '@ndvr/billing-api';

import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { formatCurrency, formatCurrencyShort } from 'utils';

import { useApplyManualFeeAmendment } from './billing-run-queries';
import { amendableFeeTypes, FEE_TYPE_DESCRIPTION } from './billing-run-table';

type Scope = 'group' | 'account';

type ManualFeeAmendmentDialogProps = {
    run: BillingGroupRun;
    accountNameByGUID: Map<string, string>;
    onClose: () => void;
};

const SECTION_LABEL_CLASSNAME = 'text-muted-foreground text-[11px] font-semibold tracking-wide uppercase';

const STRATEGY_TYPE_LABEL: Record<IntelligentStrategyType, string> = {
    [IntelligentStrategyType.PERSONALIZED_INDEXING]: 'Personalized Indexing',
    [IntelligentStrategyType.LIFESTYLE_PROTECTION]: 'Lifestyle Protection',
};

const matchesFeeGroup = (
    detail: AccountFee['feeDetails'][number],
    feeType: FeeType | undefined,
    strategyType?: IntelligentStrategyType,
) => detail.feeType === feeType && (feeType !== FeeType.INTELLIGENT_STRATEGY || detail.strategyType === strategyType);

const toCents = (amount: string) => Math.round(Number(amount) * 100);

const billingDateLabel = (billingDate: string) => {
    const [year, month] = billingDate.split('-');
    const monthName = new Date(Number(year), Number(month) - 1, 1).toLocaleString('en-US', { month: 'long' });
    return `1st ${monthName}, ${year}`;
};

export const ManualFeeAmendmentDialog = ({ run, accountNameByGUID, onClose }: ManualFeeAmendmentDialogProps) => {
    const applyAmendment = useApplyManualFeeAmendment();
    const availableFeeTypes = useMemo(() => amendableFeeTypes(run), [run]);
    const [feeType, setFeeType] = useState<FeeType | undefined>(availableFeeTypes[0]);
    const [scope, setScope] = useState<Scope>('group');
    const [accountGUID, setAccountGUID] = useState<string>();
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');

    const availableStrategyTypes = useMemo(
        () => [
            ...new Set(
                run.accountFees.flatMap((fee) =>
                    fee.feeDetails
                        .filter((detail) => detail.feeType === FeeType.INTELLIGENT_STRATEGY)
                        .map((detail) => detail.strategyType)
                        .filter((value): value is IntelligentStrategyType => value != null),
                ),
            ),
        ],
        [run],
    );
    const [strategyType, setStrategyType] = useState<IntelligentStrategyType | undefined>(availableStrategyTypes[0]);

    const selectedStrategyType = feeType === FeeType.INTELLIGENT_STRATEGY ? strategyType : undefined;
    const rows = useMemo(
        () =>
            run.accountFees
                .flatMap((fee) => {
                    const details = fee.feeDetails.filter((detail) =>
                        matchesFeeGroup(detail, feeType, selectedStrategyType),
                    );
                    if (details.length === 0) return [];
                    const totals = details.reduce(
                        (sum, detail) => {
                            const chargedCents = toCents(detail.chargedAmount);
                            return {
                                calculatedCents: sum.calculatedCents + chargedCents,
                                outstandingCents: sum.outstandingCents + chargedCents - toCents(detail.settledAmount),
                            };
                        },
                        { calculatedCents: 0, outstandingCents: 0 },
                    );
                    return [{ fee, ...totals }];
                })
                .sort(
                    (a, b) =>
                        Number(b.fee.billableAum) - Number(a.fee.billableAum) ||
                        (accountNameByGUID.get(a.fee.accountGUID) ?? a.fee.accountGUID).localeCompare(
                            accountNameByGUID.get(b.fee.accountGUID) ?? b.fee.accountGUID,
                            undefined,
                            { sensitivity: 'base' },
                        ),
                ),
        [accountNameByGUID, feeType, run, selectedStrategyType],
    );
    const totalCalculatedCents = rows.reduce((sum, row) => sum + row.calculatedCents, 0);
    const hasMixedSignCharges =
        rows.some((row) => row.calculatedCents > 0) && rows.some((row) => row.calculatedCents < 0);
    const groupScopeBlocked = scope === 'group' && hasMixedSignCharges;
    const selectedAccount = rows.find((row) => row.fee.accountGUID === accountGUID);
    const maximumCreditCents = scope === 'group' ? totalCalculatedCents : selectedAccount?.calculatedCents;
    const numericAmount = Number(amount);
    const amendmentCents = toCents(amount);
    const validAmount = amount !== '' && Number.isFinite(numericAmount) && amendmentCents !== 0;
    const creditTooLarge =
        validAmount && amendmentCents < 0 && maximumCreditCents != null && maximumCreditCents + amendmentCents < 0;
    const canSubmit =
        rows.length > 0 &&
        validAmount &&
        !creditTooLarge &&
        !groupScopeBlocked &&
        reason.trim().length > 0 &&
        (scope === 'group' || selectedAccount != null);

    const changeFeeType = (value: string) => {
        if (!value) return;
        const nextFeeType = value as FeeType;
        setFeeType(nextFeeType);
        setAccountGUID(undefined);
        if (nextFeeType === FeeType.INTELLIGENT_STRATEGY) setStrategyType(availableStrategyTypes[0]);
    };

    const submit = () => {
        if (!canSubmit || !feeType) return;
        const request: ManualFeeAmendmentRequest = {
            feeType,
            strategyType: selectedStrategyType,
            accountGUID: scope === 'account' ? accountGUID : undefined,
            amount,
            reason: reason.trim(),
        };
        applyAmendment.mutate(
            { billingGroupRunGUID: run.billingGroupRunGUID, request },
            {
                onSuccess: () => {
                    toast.success('Fee amendment posted');
                    onClose();
                },
            },
        );
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-[900px]"
                aria-describedby={undefined}
            >
                <DialogHeader className="border-b px-6 py-5 pr-12">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
                            <LuSlidersHorizontal className="h-5 w-5" />
                        </span>
                        <div className="space-y-1">
                            <DialogDescription className="text-xs font-semibold tracking-wide uppercase">
                                Amend fees · {billingDateLabel(run.billingDate)}
                            </DialogDescription>
                            <DialogTitle>{run.billingGroupName ?? run.billingGroupGUID}</DialogTitle>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5">
                    <div className="space-y-2">
                        <Label className={SECTION_LABEL_CLASSNAME}>Fee type</Label>
                        <ToggleGroup
                            type="single"
                            value={feeType}
                            onValueChange={changeFeeType}
                            className="grid w-full auto-cols-fr grid-flow-col gap-2"
                        >
                            {availableFeeTypes.map((type) => (
                                <ToggleGroupItem
                                    key={type}
                                    value={type}
                                    className="data-[state=on]:border-cocoa-800 data-[state=on]:bg-cocoa-0 data-[state=on]:text-foreground data-[state=on]:hover:bg-cocoa-0 hover:text-foreground h-auto flex-col items-start gap-0.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 hover:bg-gray-50"
                                >
                                    <span className="font-semibold">{FEE_TYPE_DESCRIPTION[type]}</span>
                                </ToggleGroupItem>
                            ))}
                        </ToggleGroup>
                    </div>

                    <div className="space-y-2">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div className="space-y-2">
                                <Label className={SECTION_LABEL_CLASSNAME}>Scope</Label>
                                {feeType === FeeType.INTELLIGENT_STRATEGY && (
                                    <Select
                                        value={strategyType}
                                        onValueChange={(value) => {
                                            setStrategyType(value as IntelligentStrategyType);
                                            setAccountGUID(undefined);
                                        }}
                                    >
                                        <SelectTrigger className="h-9 min-w-56">
                                            <SelectValue placeholder="Select strategy" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableStrategyTypes.map((type) => (
                                                <SelectItem key={type} value={type}>
                                                    {STRATEGY_TYPE_LABEL[type]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                            <ToggleGroup
                                type="single"
                                value={scope}
                                onValueChange={(value) => {
                                    if (!value) return;
                                    setScope(value as Scope);
                                    setAccountGUID(undefined);
                                }}
                                className="gap-2"
                            >
                                {(
                                    [
                                        ['group', 'Entire group'],
                                        ['account', 'Single account'],
                                    ] as const
                                ).map(([value, label]) => (
                                    <ToggleGroupItem
                                        key={value}
                                        value={value}
                                        className="data-[state=on]:border-cocoa-800 data-[state=on]:bg-cocoa-0 data-[state=on]:text-foreground text-muted-foreground hover:text-foreground h-8 rounded-md border border-gray-200 bg-white px-3 text-xs font-medium hover:bg-gray-50"
                                    >
                                        {label}
                                    </ToggleGroupItem>
                                ))}
                            </ToggleGroup>
                        </div>

                        {groupScopeBlocked && (
                            <p role="alert" className="text-destructive text-sm">
                                Fee groups with both positive and negative charges can only be amended per account
                            </p>
                        )}

                        <div className="overflow-hidden rounded-lg border">
                            <div className="text-muted-foreground grid grid-cols-[32px_minmax(0,1fr)_110px_110px_110px] gap-3 bg-gray-50 px-4 py-2 text-xs font-semibold tracking-wide uppercase">
                                <span />
                                <span>Account</span>
                                <span className="text-right">Billable NAV ↓</span>
                                <span className="text-right">Calculated</span>
                                <span className="text-right">Outstanding</span>
                            </div>
                            {rows.length === 0 ? (
                                <div className="text-muted-foreground px-4 py-6 text-center text-sm">
                                    No accounts in this group were charged this fee type.
                                </div>
                            ) : (
                                <RadioGroup value={accountGUID} onValueChange={setAccountGUID}>
                                    {rows.map(({ fee, calculatedCents, outstandingCents }) => {
                                        const selected = scope === 'account' && fee.accountGUID === accountGUID;
                                        const receivesGroupAllocation =
                                            totalCalculatedCents === 0 || calculatedCents !== 0;
                                        const noGroupAllocation = scope === 'group' && !receivesGroupAllocation;
                                        return (
                                            <Label
                                                key={fee.accountGUID}
                                                htmlFor={`amend-${fee.accountGUID}`}
                                                className={`grid grid-cols-[32px_minmax(0,1fr)_110px_110px_110px] items-center gap-3 border-t px-4 py-3 font-normal first:border-t-0 ${
                                                    selected ? 'bg-cocoa-0' : ''
                                                } ${scope === 'account' ? 'cursor-pointer hover:bg-gray-50' : ''} ${noGroupAllocation ? 'text-muted-foreground' : ''}`}
                                            >
                                                {scope === 'account' ? (
                                                    <RadioGroupItem
                                                        className="justify-self-center"
                                                        id={`amend-${fee.accountGUID}`}
                                                        value={fee.accountGUID}
                                                    />
                                                ) : receivesGroupAllocation ? (
                                                    <LuCheck className="text-muted-foreground h-4 w-4 justify-self-center" />
                                                ) : (
                                                    <span className="text-muted-foreground justify-self-center">—</span>
                                                )}
                                                <span className="flex min-w-0 flex-col">
                                                    <span className="truncate">
                                                        {accountNameByGUID.get(fee.accountGUID) ?? fee.accountGUID}
                                                    </span>
                                                    {noGroupAllocation && (
                                                        <span className="text-muted-foreground text-xs">
                                                            No allocation
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="text-right tabular-nums">
                                                    {formatCurrencyShort(fee.billableAum)}
                                                </span>
                                                <span className="text-right tabular-nums">
                                                    {formatCurrency(calculatedCents / 100)}
                                                </span>
                                                <span className="text-right tabular-nums">
                                                    {formatCurrency(outstandingCents / 100)}
                                                </span>
                                            </Label>
                                        );
                                    })}
                                </RadioGroup>
                            )}
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
                        <div className="space-y-2">
                            <Label htmlFor="amendment-amount" className={SECTION_LABEL_CLASSNAME}>
                                Amount*
                            </Label>
                            <NumberInput
                                id="amendment-amount"
                                value={amount}
                                onValueChange={(values) => setAmount(values.value)}
                                prefix="$"
                                thousandSeparator
                                allowNegative
                                decimalScale={2}
                                placeholder="$0.00"
                            />
                            <p className={`text-xs ${creditTooLarge ? 'text-destructive' : 'text-muted-foreground'}`}>
                                {creditTooLarge && maximumCreditCents != null
                                    ? maximumCreditCents < 0
                                        ? 'A negative calculated fee cannot be reduced further.'
                                        : `Credit cannot exceed the ${scope === 'group' ? "group's" : "account's"} calculated fee of ${formatCurrency(maximumCreditCents / 100)}.`
                                    : 'Use a negative amount for a credit.'}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="amendment-reason" className={SECTION_LABEL_CLASSNAME}>
                                Note*
                            </Label>
                            <Textarea
                                id="amendment-reason"
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                                placeholder="Reason for the amendment"
                                maxLength={1024}
                                className="min-h-20 resize-none"
                            />
                            <p className="text-muted-foreground text-xs">
                                {scope === 'group'
                                    ? totalCalculatedCents !== 0
                                        ? 'Distributed proportionally based on each account’s calculated fee magnitude. Accounts with a $0 calculated fee receive no allocation.'
                                        : `Distributed evenly across ${rows.length} eligible ${rows.length === 1 ? 'account' : 'accounts'} because the calculated fee total is $0.`
                                    : accountGUID
                                      ? 'Applied in full to the selected account.'
                                      : 'Select an account above.'}
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter className="border-t px-6 py-4">
                    <Button variant="ghost" onClick={onClose} disabled={applyAmendment.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit || applyAmendment.isPending}>
                        {applyAmendment.isPending ? 'Posting…' : 'Post amendment'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
